type FacetLike = {
  name?: string | null;
  values?: Array<string | null> | null;
};

export type AttributeVisibilityContext = {
  categoryPath?: string[] | null;
  ageGroups?: string[] | null;
  facets?: FacetLike[] | null;
  additionalSignals?: string[] | null;
  explicitMaternity?: boolean;
};

const BABY_TODDLER_ONLY_ATTRIBUTES = new Set([
  "baby_unique_features",
  "baby_toddler_sleepwear_style",
]);
const MATERNITY_ONLY_ATTRIBUTE = "maternity_unique_features";

const INFANT_TODDLER_TERMS = new Set([
  "infant",
  "infants",
  "toddler",
  "toddlers",
  "kid",
  "kids",
  "child",
  "children",
  "baby",
  "babies",
  "newborn",
  "newborns",
]);
const CATEGORY_PATH_PREFIX = "category_path_depth_";
const AGE_FACET_NAME_REGEX = /(^|_)age_groups?$/;

export function normalizeSignal(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function toSignalParts(value: string): string[] {
  return normalizeSignal(value)
    .split("_")
    .map((part) => part.trim())
    .filter(Boolean);
}

function isInfantOrToddlerSignal(value: string): boolean {
  const normalized = normalizeSignal(value);
  if (INFANT_TODDLER_TERMS.has(normalized)) return true;
  const parts = toSignalParts(value);
  return parts.some((part) => INFANT_TODDLER_TERMS.has(part));
}

function isMaternitySignal(value: string): boolean {
  const normalized = normalizeSignal(value);
  if (normalized === "maternity") return true;
  const parts = toSignalParts(value);
  return parts.includes("maternity");
}

function collectSignals(context: AttributeVisibilityContext): string[] {
  const categorySignals = Array.isArray(context.categoryPath) ? context.categoryPath : [];
  const ageSignals = Array.isArray(context.ageGroups) ? context.ageGroups : [];
  const additionalSignals = Array.isArray(context.additionalSignals)
    ? context.additionalSignals
    : [];
  const facetSignals = (Array.isArray(context.facets) ? context.facets : []).flatMap((facet) => {
    const name = facet?.name ? [facet.name] : [];
    const values = Array.isArray(facet?.values)
      ? facet.values.filter((value): value is string => typeof value === "string")
      : [];
    return [...name, ...values];
  });

  return [...categorySignals, ...ageSignals, ...additionalSignals, ...facetSignals];
}

function getInfantToddlerSignals(context: AttributeVisibilityContext): string[] {
  const categorySignals = Array.isArray(context.categoryPath) ? context.categoryPath : [];
  const ageSignals = Array.isArray(context.ageGroups) ? context.ageGroups : [];
  const facetSignals = (Array.isArray(context.facets) ? context.facets : []).flatMap((facet) => {
    const facetName = String(facet?.name || "");
    const normalizedFacetName = normalizeSignal(facetName);
    const values = Array.isArray(facet?.values)
      ? facet.values.filter((value): value is string => typeof value === "string")
      : [];
    if (
      normalizedFacetName.startsWith(CATEGORY_PATH_PREFIX) ||
      AGE_FACET_NAME_REGEX.test(normalizedFacetName)
    ) {
      return [facetName, ...values];
    }
    return [];
  });

  // Intentionally avoid `additionalSignals` to prevent false positives from fashion terms
  // like "baby tee" in adult contexts.
  return [...categorySignals, ...ageSignals, ...facetSignals];
}

export function shouldShowAttributeForContext(
  attributeKey: string,
  context: AttributeVisibilityContext = {}
): boolean {
  const normalizedAttribute = normalizeSignal(attributeKey);
  const signals = collectSignals(context);
  const infantToddlerSignals = getInfantToddlerSignals(context);

  const hasInfantOrToddlerContext = infantToddlerSignals.some(isInfantOrToddlerSignal);
  const hasExplicitMaternityContext = context.explicitMaternity ?? signals.some(isMaternitySignal);

  if (BABY_TODDLER_ONLY_ATTRIBUTES.has(normalizedAttribute)) {
    return hasInfantOrToddlerContext;
  }

  if (normalizedAttribute === MATERNITY_ONLY_ATTRIBUTE) {
    return hasExplicitMaternityContext;
  }

  return true;
}
